import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getChatCompletion } from '../../services/aiIntegrations/chatCompletion';
import toast from 'react-hot-toast';
import BrandingPanel from './components/BrandingPanel';
import SmartDocumentPreview from './components/SmartDocumentPreview';
import RichTextEditor, { textToHtml, htmlToText } from './components/RichTextEditor';

const TEMPLATE_TYPES = [
  { value: 'agreement', label: 'Agreement', icon: 'FileSignature', color: 'bg-blue-100 text-blue-700', accent: '#2563eb' },
  { value: 'estimate', label: 'Estimate', icon: 'Calculator', color: 'bg-purple-100 text-purple-700', accent: '#7c3aed' },
  { value: 'invoice', label: 'Invoice', icon: 'Receipt', color: 'bg-emerald-100 text-emerald-700', accent: '#059669' },
  { value: 'email', label: 'Email', icon: 'Mail', color: 'bg-amber-100 text-amber-700', accent: '#d97706' },
  { value: 'sms', label: 'SMS', icon: 'MessageSquare', color: 'bg-pink-100 text-pink-700', accent: '#be185d' },
];

const EDITOR_TABS = [
  { id: 'content', label: 'Content', icon: 'FileText' },
  { id: 'branding', label: 'Branding & Logo', icon: 'Image' },
  { id: 'preview', label: 'Preview', icon: 'Eye' },
];

const EMPTY_TEMPLATE = {
  name: '',
  template_type: 'agreement',
  content: '',
  is_default: false,
  brand_color: '#2563eb',
  pricing_type: 'flat_rate',
  show_logo: true,
  font_style: 'professional',
  line_items: [],
  tax_enabled: false,
  tax_rate: 0,
  payment_terms: 'Due upon completion',
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TemplatesPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('all');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seedingDefaults, setSeedingDefaults] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [activeEditorTab, setActiveEditorTab] = useState('content');
  const { profile, organization } = useAuth();

  // Use a ref to hold the latest editingTemplate to avoid stale closures
  const editingRef = useRef(editingTemplate);
  useEffect(() => { editingRef.current = editingTemplate; }, [editingTemplate]);

  const fetchTemplates = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('templates')?.select('*')?.eq('org_id', profile?.org_id)?.is('deleted_at', null)?.order('created_at', { ascending: false });
      if (error) throw error;
      setTemplates(data || []);
    } catch (err) {
      toast?.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const updateTemplate = useCallback((updates) => {
    setEditingTemplate(prev => ({ ...prev, ...updates }));
  }, []);

  const handleSaveTemplate = async () => {
    if (!editingTemplate?.name?.trim() || !editingTemplate?.template_type) {
      toast?.error('Please fill in template name and type');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        org_id: profile?.org_id,
        name: editingTemplate?.name,
        template_type: editingTemplate?.template_type,
        content: editingTemplate?.content || '',
        is_default: editingTemplate?.is_default || false,
        variables: [],
        logo_url: editingTemplate?.logo_url || null,
        brand_color: editingTemplate?.brand_color || '#2563eb',
        show_logo: editingTemplate?.show_logo !== false,
        font_style: editingTemplate?.font_style || 'professional',
        pricing_type: editingTemplate?.pricing_type || 'flat_rate',
        line_items: editingTemplate?.line_items || [],
        tax_rate: editingTemplate?.tax_rate || 0,
        tax_enabled: editingTemplate?.tax_enabled || false,
        notes: editingTemplate?.notes || '',
        payment_terms: editingTemplate?.payment_terms || 'Due upon completion',
      };

      if (editingTemplate?.id) {
        const { error } = await supabase?.from('templates')?.update(payload)?.eq('id', editingTemplate?.id);
        if (error) throw error;
        toast?.success('Template updated!');
      } else {
        const { error } = await supabase?.from('templates')?.insert(payload);
        if (error) throw error;
        toast?.success('Template created!');
      }
      setIsEditorOpen(false);
      setEditingTemplate(null);
      setActiveEditorTab('content');
      fetchTemplates();
    } catch (err) {
      toast?.error(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await supabase?.from('templates')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', templateId);
      toast?.success('Moved to Trash');
      fetchTemplates();
    } catch (err) {
      toast?.error('Failed to delete');
    }
  };

  const handleSeedDefaults = async () => {
    setSeedingDefaults(true);
    try {
      const orgName = organization?.name || 'Your Company';
      const brandColor = '#2563eb';

      const defaults = [
        {
          org_id: profile?.org_id,
          name: 'Service Agreement',
          template_type: 'agreement',
          is_default: true,
          brand_color: brandColor,
          pricing_type: 'flat_rate',
          payment_terms: 'Due upon completion',
          font_style: 'professional',
          show_logo: true,
          line_items: [
            { id: 1, label: 'Service', variable: '{{labor_cost}}', type: 'flat' },
            { id: 2, label: 'Materials', variable: '{{materials_cost}}', type: 'flat' },
          ],
          tax_enabled: false,
          tax_rate: 0,
          content: `<h1>SERVICE AGREEMENT</h1><hr/><p><strong>COMPANY:</strong> {{company_name}}</p><p><strong>CLIENT:</strong> {{client_name}}</p><p><strong>DATE:</strong> {{date}}</p><p><strong>SCHEDULED:</strong> {{scheduled_date}}</p><hr/><h2>SERVICE DETAILS</h2><p><strong>Service:</strong> {{service_description}}</p><p><strong>Location:</strong> {{location}}</p><hr/><h2>TERMS &amp; CONDITIONS</h2><ol><li>Payment is due upon completion of services.</li><li>${orgName} is not responsible for pre-existing conditions.</li><li>Client agrees to provide safe and accessible work area.</li><li>Cancellations must be made at least 24 hours in advance.</li><li>Any additional work beyond the agreed scope will be quoted separately.</li></ol><hr/><h2>SIGNATURES</h2><p>Client Signature: <span style="display:inline-block;min-width:200px;border-bottom:1px solid #374151;">&nbsp;</span>&nbsp;&nbsp;Date: <span style="display:inline-block;min-width:100px;border-bottom:1px solid #374151;">&nbsp;</span></p><p style="font-size:12px;color:#6b7280;">Client: {{client_name}}</p><p style="margin-top:16px;">${orgName} Representative: <span style="display:inline-block;min-width:200px;border-bottom:1px solid #374151;">&nbsp;</span>&nbsp;&nbsp;Date: <span style="display:inline-block;min-width:100px;border-bottom:1px solid #374151;">&nbsp;</span></p>`,
        },
        {
          org_id: profile?.org_id,
          name: 'Standard Estimate',
          template_type: 'estimate',
          is_default: true,
          brand_color: '#7c3aed',
          pricing_type: 'flat_rate',
          payment_terms: 'Net 30',
          font_style: 'professional',
          show_logo: true,
          line_items: [
            { id: 1, label: 'Service', variable: '{{labor_cost}}', type: 'flat' },
            { id: 2, label: 'Materials', variable: '{{materials_cost}}', type: 'flat' },
          ],
          tax_enabled: false,
          tax_rate: 0,
          content: `<h1>ESTIMATE</h1><hr/><p><strong>FROM:</strong> {{company_name}}</p><p><strong>TO:</strong> {{client_name}}</p><p><strong>ESTIMATE #:</strong> {{estimate_number}}</p><p><strong>DATE:</strong> {{date}}</p><hr/><h2>SERVICE</h2><p><strong>Service:</strong> {{service_description}}</p><p><strong>Location:</strong> {{location}}</p><p><strong>Scheduled:</strong> {{scheduled_date}}</p><hr/><p>This estimate is valid for 30 days from the date above. To accept, please sign below or reply to confirm.</p><p style="margin-top:24px;">Accepted by: <span style="display:inline-block;min-width:200px;border-bottom:1px solid #374151;">&nbsp;</span>&nbsp;&nbsp;Date: <span style="display:inline-block;min-width:100px;border-bottom:1px solid #374151;">&nbsp;</span></p>`,
        },
        {
          org_id: profile?.org_id,
          name: 'Standard Invoice',
          template_type: 'invoice',
          is_default: true,
          brand_color: '#059669',
          pricing_type: 'flat_rate',
          payment_terms: 'Net 30',
          font_style: 'professional',
          show_logo: true,
          line_items: [
            { id: 1, label: 'Service', variable: '{{labor_cost}}', type: 'flat' },
            { id: 2, label: 'Materials', variable: '{{materials_cost}}', type: 'flat' },
          ],
          tax_enabled: true,
          tax_rate: 10,
          content: `<h1>INVOICE</h1><hr/><p><strong>FROM:</strong> {{company_name}}</p><p><strong>TO:</strong> {{client_name}}</p><p><strong>INVOICE #:</strong> {{invoice_number}}</p><p><strong>DATE:</strong> {{date}}</p><p><strong>DUE DATE:</strong> {{due_date}}</p><hr/><h2>SERVICE</h2><p><strong>Service:</strong> {{service_description}}</p><p><strong>Location:</strong> {{location}}</p><p><strong>Duration:</strong> {{duration}} hour(s)</p><p><strong>Payment Status:</strong> {{payment_status}}</p><hr/><p>Please make payment by {{due_date}}. Thank you for choosing {{company_name}}!</p>`,
        },
        {
          org_id: profile?.org_id,
          name: 'Job Confirmation Email',
          template_type: 'email',
          is_default: true,
          brand_color: '#d97706',
          font_style: 'professional',
          show_logo: true,
          line_items: [],
          tax_enabled: false,
          tax_rate: 0,
          content: `<p><strong>Subject:</strong> Your Appointment Confirmation — {{company_name}}</p><hr/><p>Hi {{client_name}},</p><p>Thank you for choosing {{company_name}}! Your appointment has been confirmed.</p><ul><li>📅 <strong>Date:</strong> {{scheduled_date}}</li><li>📍 <strong>Location:</strong> {{location}}</li><li>🔧 <strong>Service:</strong> {{service_description}}</li><li>💰 <strong>Estimated Total:</strong> $&#123;&#123;total_cost&#125;&#125;</li></ul><p>If you have any questions or need to reschedule, please contact us as soon as possible.</p><p>We look forward to serving you!</p><p>Best regards,<br/><strong>{{company_name}} Team</strong></p>`,
        },
        {
          org_id: profile?.org_id,
          name: 'Appointment Reminder SMS',
          template_type: 'sms',
          is_default: true,
          brand_color: '#be185d',
          font_style: 'professional',
          line_items: [],
          tax_enabled: false,
          tax_rate: 0,
          content: `Hi {{client_name}}, reminder from {{company_name}}: appointment on {{scheduled_date}} at {{location}}. Reply STOP to opt out.`,
        },
      ];

      const { error } = await supabase?.from('templates')?.insert(defaults);
      if (error) throw error;
      toast?.success(`${defaults?.length} smart templates created for ${orgName}!`);
      fetchTemplates();
    } catch (err) {
      toast?.error('Failed to create default templates: ' + err?.message);
    } finally {
      setSeedingDefaults(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!editingTemplate?.template_type) return;
    setAiGenerating(true);
    try {
      const orgName = organization?.name || 'Your Company';
      const businessType = organization?.business_type || 'service';
      const serviceTypes = organization?.settings?.service_types?.join(', ') || 'general services';
      const templateLabel = TEMPLATE_TYPES?.find(t => t?.value === editingTemplate?.template_type)?.label || editingTemplate?.template_type;

      const systemPrompt = `You are a professional business document writer for a CRM system. Generate clean, complete HTML document templates using semantic HTML tags (h1, h2, p, ul, ol, li, table, hr, strong, em). 

CRITICAL RULES:
1. Use {{variable_name}} placeholders for ALL dynamic data — never hardcode values
2. The template must include ALL relevant variables from the job entity
3. For pricing: use BOTH flat rate AND hourly variables — the system auto-selects the right one at runtime
4. Return ONLY the HTML content — no markdown, no code blocks, no explanations
5. Make it professional, complete, and ready to use`;

      const allVariables = `
AVAILABLE VARIABLES — use ALL that are relevant:

CLIENT & COMPANY:
- {{company_name}} — your business name
- {{client_name}} — customer full name  
- {{date}} — document date
- {{due_date}} — payment due date
- {{invoice_number}} — auto-generated invoice number
- {{estimate_number}} — auto-generated estimate number

JOB DETAILS:
- {{job_title}} — job title/name
- {{service_description}} — what was done / service details
- {{service_type}} — type of service
- {{location}} — service address
- {{scheduled_date}} — appointment date/time
- {{duration}} — hours worked (hourly jobs)
- {{frequency}} — recurring frequency (cleaning, landscaping, etc.)
- {{home_size}} — property size
- {{issue_type}} — issue/task type (HVAC, plumbing, handyman)
- {{system_type}} — system type (HVAC)
- {{move_from}} — origin address (moving jobs)
- {{move_to}} — destination address (moving jobs)
- {{vehicle_make_model}} — vehicle (auto detailing)
- {{vehicle_year}} — vehicle year
- {{vehicle_type}} — vehicle type
- {{service_location}} — where service is performed

PRICING (auto-filled from job — flat rate OR hourly):
- {{total_cost}} — job total (flat rate)
- {{labor_cost}} — labor cost
- {{materials_cost}} — materials/parts cost
- {{hourly_rate}} — rate per hour (hourly jobs)
- {{subtotal}} — before tax
- {{tax}} — tax amount
- {{total}} — grand total including tax
- {{payment_status}} — Paid / Unpaid / Partial
- {{payment_terms}} — e.g. Net 30, Due on completion`;

      const typeInstructions = {
        agreement: `Generate a complete SERVICE AGREEMENT that includes:
- Header with company name, client name, date, job reference
- Service details section (service description, location, scheduled date, relevant job-specific fields)
- Pricing section with a table showing labor, materials, and total (use both {{labor_cost}}, {{materials_cost}}, {{total_cost}})
- Terms & Conditions (5-8 relevant clauses for ${businessType})
- Signature lines for both client and company representative`,

        estimate: `Generate a complete ESTIMATE document that includes:
- Header with estimate number, date, company and client info
- Service details section (what will be done, where, when)
- Pricing breakdown table with labor ({{labor_cost}}), materials ({{materials_cost}}), subtotal ({{subtotal}}), tax ({{tax}}), total ({{total}})
- For hourly jobs: show {{duration}} hours × {{hourly_rate}}/hr = {{labor_cost}}
- Validity note (estimate valid for 30 days)
- Acceptance signature line`,

        invoice: `Generate a complete INVOICE that includes:
- Header with invoice number ({{invoice_number}}), date, due date, company and client info
- Service rendered section (description, location, date performed)
- Pricing table: labor ({{labor_cost}}), materials ({{materials_cost}}), subtotal ({{subtotal}}), tax ({{tax}}), TOTAL ({{total}})
- For hourly: {{duration}} hours × {{hourly_rate}}/hr
- Payment status: {{payment_status}}
- Payment instructions and terms ({{payment_terms}})
- Thank you note`,

        email: `Generate a professional EMAIL TEMPLATE with subject line and body. Include greeting, service confirmation details, pricing summary, and call to action.`,

        sms: `Generate a concise SMS template (under 160 chars) with key job info.`,
      };

      const userPrompt = `Generate a professional ${templateLabel} HTML template for "${orgName}", a ${businessType} business offering: ${serviceTypes}.

${allVariables}

TEMPLATE REQUIREMENTS:
${typeInstructions?.[editingTemplate?.template_type] || typeInstructions?.agreement}

Industry context: ${businessType} — make the language, terms, and fields specific to this industry.
Include ALL relevant variables from the list above. The more variables used, the more useful the template.`;

      const response = await getChatCompletion(
        'OPEN_AI',
        'gpt-4.1',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        { max_tokens: 3000 }
      );

      const generatedContent = response?.choices?.[0]?.message?.content;
      if (generatedContent) {
        const cleanContent = generatedContent
          ?.replace(/^```html\s*/i, '')
          ?.replace(/^```\s*/i, '')
          ?.replace(/\s*```$/i, '')
          ?.trim();

        setEditingTemplate(prev => ({ ...prev, content: cleanContent }));
        toast?.success('Template generated with all job variables!');
        setActiveEditorTab('content');
      } else {
        toast?.error('No content returned from AI');
      }
    } catch (err) {
      toast?.error('AI generation failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setAiGenerating(false);
    }
  };

  const filteredTemplates = activeType === 'all' ? templates : templates?.filter(t => t?.template_type === activeType);
  const getTypeCfg = (type) => TEMPLATE_TYPES?.find(t => t?.value === type) || TEMPLATE_TYPES?.[0];

  // ─── Editor View ─────────────────────────────────────────────────────────────
  if (isEditorOpen && editingTemplate) {
    const typeCfg = getTypeCfg(editingTemplate?.template_type);
    const isSMS = editingTemplate?.template_type === 'sms';

    return (
      <>
        <Helmet><title>Edit Template — Wilcart CRM</title></Helmet>
        <div className="min-h-screen bg-background">
          <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
          <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
          <main className="lg:ml-64 pt-24 lg:pt-28">
            <div className="p-4 lg:p-6">

              {/* Top Bar */}
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => { setIsEditorOpen(false); setEditingTemplate(null); setActiveEditorTab('content'); }}
                    className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    <Icon name="ArrowLeft" size={20} />
                  </button>
                  <div>
                    <h1 className="text-xl font-semibold text-foreground">
                      {editingTemplate?.id ? 'Edit Template' : 'New Template'}
                    </h1>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeCfg?.color}`}>
                        <Icon name={typeCfg?.icon} size={10} className="mr-1" />
                        {typeCfg?.label}
                      </span>
                      <span className="text-xs text-muted-foreground">{organization?.name}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleAIGenerate}
                    disabled={aiGenerating}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-60 bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
                  >
                    {aiGenerating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Generating…</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Sparkles" size={15} />
                        <span>AI Generate</span>
                      </>
                    )}
                  </button>
                  <Button onClick={handleSaveTemplate} loading={saving} disabled={saving}>
                    <Icon name="Save" size={15} className="mr-1.5" />
                    Save
                  </Button>
                </div>
              </div>

              {/* Meta Row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Template Name *</label>
                  <input
                    type="text"
                    value={editingTemplate?.name || ''}
                    onChange={e => updateTemplate({ name: e?.target?.value })}
                    placeholder="e.g. Residential Security Agreement"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Document Type</label>
                  <select
                    value={editingTemplate?.template_type || 'agreement'}
                    onChange={e => updateTemplate({ template_type: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  >
                    {TEMPLATE_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2 mb-5">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={editingTemplate?.is_default || false}
                  onChange={e => updateTemplate({ is_default: e?.target?.checked })}
                  className="w-4 h-4 text-primary border-border rounded"
                />
                <label htmlFor="is_default" className="text-sm text-foreground">
                  Set as default {typeCfg?.label} template for <strong>{organization?.name}</strong>
                </label>
              </div>

              {/* Editor Tabs */}
              <div className="flex border-b border-border mb-5 overflow-x-auto">
                {EDITOR_TABS?.map(tab => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveEditorTab(tab?.id)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                      activeEditorTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={14} />
                    {tab?.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {/* Left Panel */}
                <div>
                  {activeEditorTab === 'content' && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-muted-foreground">
                          {isSMS ? 'SMS Content' : 'Document Editor'}
                        </label>
                        {!isSMS && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Icon name="Type" size={11} />
                            Rich text editor — format like Word
                          </span>
                        )}
                      </div>

                      {/* Smart Variables Hint */}
                      {!isSMS && (
                        <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-3.5 py-2.5 mb-3">
                          <Icon name="Zap" size={14} className="text-blue-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-blue-800">Smart Variables — Auto-filled from Job</p>
                            <p className="text-xs text-blue-600 mt-0.5">
                              Click <strong>Variables</strong> in the toolbar to insert job fields. Use <code className="bg-blue-100 px-1 rounded font-mono">{'{{total_cost}}'}</code> for flat rate or <code className="bg-blue-100 px-1 rounded font-mono">{'{{duration}}'}</code> × <code className="bg-blue-100 px-1 rounded font-mono">{'{{hourly_rate}}'}</code> for hourly — all values auto-fill from the job when the document is generated. Click <strong>AI Generate</strong> to auto-create a complete template with all variables.
                            </p>
                          </div>
                        </div>
                      )}

                      {isSMS ? (
                        <textarea
                          value={editingTemplate?.content || ''}
                          onChange={e => updateTemplate({ content: e?.target?.value })}
                          rows={6}
                          maxLength={160}
                          className="w-full px-4 py-3 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-y font-mono leading-relaxed"
                          placeholder="Write your SMS template here. Use {{variable_name}} for dynamic fields."
                          spellCheck={false}
                        />
                      ) : (
                        <RichTextEditor
                          key={editingTemplate?.id || 'new'}
                          value={editingTemplate?.content || ''}
                          onChange={(html) => updateTemplate({ content: html })}
                          placeholder={`Write your ${typeCfg?.label?.toLowerCase()} template here.\n\nUse the toolbar to format text, insert tables, signature lines, and variables.\nOr click "AI Generate" to auto-create a complete template with all job variables pre-inserted.`}
                          minHeight={420}
                        />
                      )}

                      {isSMS && (
                        <p className="text-xs text-muted-foreground mt-1.5">
                          {editingTemplate?.content?.length || 0}/160 characters
                        </p>
                      )}
                    </div>
                  )}

                  {activeEditorTab === 'branding' && (
                    <div className="bg-card border border-border rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="Image" size={16} className="text-primary" />
                        Logo & Document Style
                      </h3>
                      <BrandingPanel
                        template={editingTemplate}
                        onChange={updateTemplate}
                        orgId={profile?.org_id}
                      />
                    </div>
                  )}

                  {activeEditorTab === 'preview' && (
                    <div className="xl:hidden">
                      <SmartDocumentPreview
                        template={editingTemplate}
                        orgName={organization?.name}
                      />
                    </div>
                  )}
                </div>

                {/* Right: Live Preview (always visible on xl) */}
                <div className="hidden xl:block">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-muted-foreground">Live Preview</label>
                    <span className="text-xs text-muted-foreground">Sample job data applied</span>
                  </div>
                  <div className="overflow-y-auto max-h-[720px] rounded-xl">
                    <SmartDocumentPreview
                      template={editingTemplate}
                      orgName={organization?.name}
                    />
                  </div>
                </div>
              </div>

            </div>
          </main>
        </div>
      </>
    );
  }

  // ─── Templates List View ──────────────────────────────────────────────────────
  return (
    <>
      <Helmet><title>Templates — Wilcart CRM</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Templates</h1>
                <p className="text-muted-foreground mt-1 text-sm">
                  Smart contracts, invoices & estimates for{' '}
                  <strong>{organization?.name || 'your organization'}</strong>
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {[
                    { icon: 'Type', label: 'Word-like Editor' },
                    { icon: 'Palette', label: 'Custom Branding' },
                    { icon: 'Clock', label: 'Hourly & Flat Rate' },
                    { icon: 'Sparkles', label: 'AI-Powered' },
                  ]?.map(badge => (
                    <span key={badge?.label} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/5 text-primary rounded-full text-xs font-medium border border-primary/10">
                      <Icon name={badge?.icon} size={10} />
                      {badge?.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-2 shrink-0">
                {templates?.length === 0 && !loading && (
                  <Button variant="outline" onClick={handleSeedDefaults} loading={seedingDefaults} disabled={seedingDefaults}>
                    <Icon name="Sparkles" size={16} className="mr-2" />
                    Create Defaults
                  </Button>
                )}
                <Button onClick={() => {
                  setEditingTemplate({ ...EMPTY_TEMPLATE });
                  setIsEditorOpen(true);
                  setActiveEditorTab('content');
                }}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  New Template
                </Button>
              </div>
            </div>

            {/* Type Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setActiveType('all')}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${activeType === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
              >
                All ({templates?.length})
              </button>
              {TEMPLATE_TYPES?.map(t => (
                <button
                  key={t?.value}
                  onClick={() => setActiveType(t?.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${activeType === t?.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                >
                  {t?.label} ({templates?.filter(tmpl => tmpl?.template_type === t?.value)?.length})
                </button>
              ))}
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6]?.map(i => <div key={i} className="h-52 bg-muted animate-pulse rounded-xl" />)}
              </div>
            ) : filteredTemplates?.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={32} className="text-primary" />
                </div>
                <p className="text-foreground font-semibold text-lg">No templates yet</p>
                <p className="text-sm text-muted-foreground mt-1 mb-6 max-w-sm mx-auto">
                  Create custom documents with a Word-like editor, your logo, brand colors, and smart pricing for{' '}
                  <strong>{organization?.name || 'your organization'}</strong>
                </p>
                <div className="flex items-center justify-center space-x-3 flex-wrap gap-2">
                  <Button variant="outline" onClick={handleSeedDefaults} loading={seedingDefaults} disabled={seedingDefaults}>
                    <Icon name="Sparkles" size={16} className="mr-2" />
                    Create Smart Defaults
                  </Button>
                  <Button onClick={() => {
                    setEditingTemplate({ ...EMPTY_TEMPLATE });
                    setIsEditorOpen(true);
                  }}>
                    <Icon name="Plus" size={16} className="mr-2" />
                    New Template
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates?.map(template => {
                  const typeCfg = getTypeCfg(template?.template_type);
                  const brandColor = template?.brand_color || typeCfg?.accent;
                  const hasLogo = template?.logo_url && template?.show_logo !== false;
                  const isHourly = template?.pricing_type === 'hourly';
                  const hasLineItems = template?.line_items?.length > 0;

                  return (
                    <div
                      key={template?.id}
                      className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-all group cursor-pointer"
                      onClick={() => { setEditingTemplate(template); setIsEditorOpen(true); setActiveEditorTab('content'); }}
                    >
                      <div className="h-1.5" style={{ backgroundColor: brandColor }} />
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2 min-w-0">
                            <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: brandColor + '15' }}>
                              <Icon name={typeCfg?.icon} size={16} style={{ color: brandColor }} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">{template?.name}</h3>
                              <div className="flex items-center flex-wrap gap-1 mt-0.5">
                                <span className="px-1.5 py-0.5 text-xs rounded-full font-medium" style={{ backgroundColor: brandColor + '15', color: brandColor }}>
                                  {typeCfg?.label}
                                </span>
                                {template?.is_default && (
                                  <span className="px-1.5 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full font-medium">Default</span>
                                )}
                                {isHourly && (
                                  <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full font-medium flex items-center gap-0.5">
                                    <Icon name="Clock" size={9} />
                                    Hourly
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2" onClick={e => e?.stopPropagation()}>
                            <button
                              onClick={() => { setEditingTemplate(template); setIsEditorOpen(true); setActiveEditorTab('content'); }}
                              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            >
                              <Icon name="Pencil" size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteTemplate(template?.id)}
                              className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Icon name="Trash2" size={13} />
                            </button>
                          </div>
                        </div>

                        {/* Mini document preview */}
                        <div className="bg-white border border-gray-100 rounded-lg p-3 mb-3 overflow-hidden" style={{ minHeight: '72px' }}>
                          <div className="h-1 rounded mb-2" style={{ backgroundColor: brandColor, width: '60%' }} />
                          {hasLogo && (
                            <div className="flex items-center gap-1 mb-1.5">
                              <div className="w-4 h-4 rounded" style={{ backgroundColor: brandColor + '20' }}>
                                <Icon name="Image" size={10} style={{ color: brandColor }} className="m-0.5" />
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded w-16" />
                            </div>
                          )}
                          <div className="space-y-1">
                            {/* Strip HTML tags for mini preview lines */}
                            {(template?.content || '')?.replace(/<[^>]+>/g, ' ')?.replace(/\s+/g, ' ')?.trim()?.split(' ')?.slice(0, 20)?.join(' ')?.split(/[.!?]/)?.slice(0, 3)?.map((line, i) => (
                              line?.trim() ? (
                                <div
                                  key={i}
                                  className="h-1.5 rounded bg-gray-100"
                                  style={{ width: `${Math.min(90, 40 + (i * 15))}%` }}
                                />
                              ) : null
                            ))}
                          </div>
                          {hasLineItems && (
                            <div className="mt-1.5 flex items-center gap-1">
                              <Icon name="DollarSign" size={9} style={{ color: brandColor }} />
                              <span className="text-xs" style={{ color: brandColor, fontSize: '9px' }}>
                                {template?.line_items?.length} line item{template?.line_items?.length !== 1 ? 's' : ''}
                                {template?.tax_enabled ? ` + ${template?.tax_rate}% tax` : ''}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {hasLogo && <Icon name="Image" size={11} className="text-muted-foreground" title="Has logo" />}
                            <p className="text-xs text-muted-foreground">
                              {new Date(template.updated_at || template.created_at)?.toLocaleDateString()}
                            </p>
                          </div>
                          <span className="text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center" style={{ color: brandColor }}>
                            Edit <Icon name="ChevronRight" size={12} className="ml-0.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default TemplatesPage;
